import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import { EngineTrackResponseExpanded } from '@engine/models/tracking'

import { BulkUpdateToolbar } from '#components/BulkUpdateToolbar'
import { Table } from '#components/Table'
import { useBulkUpdate } from '#hooks/useBulkUpdate'

import { TrackingStatusBadge } from './TrackingStatusBadge'

interface TrackingsListProps {
  trackings: EngineTrackResponseExpanded[]
  selectedId?: string
  onSelect: (tracking: EngineTrackResponseExpanded) => void
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
  sortBy?: string
  onSortChange?: (sortBy: string) => void
  onUpdatesTriggered?: () => void
}

const columnHelper = createColumnHelper<EngineTrackResponseExpanded>()

const getUpdateEndpoint = (id: string) => `trackings/${id}/update`

const TrackingsList = ({
  trackings,
  selectedId,
  onSelect,
  infiniteScrollRef,
  isFetchingMore,
  sortBy,
  onSortChange,
  onUpdatesTriggered,
}: TrackingsListProps) => {
  const visibleTrackingIds = useMemo(
    () => trackings.map(tracking => tracking._id),
    [trackings],
  )

  const bulkUpdate = useBulkUpdate({
    visibleIds: visibleTrackingIds,
    getEndpoint: getUpdateEndpoint,
    noun: 'tracking',
    onUpdatesTriggered,
  })
  const { selectedIds, toggleSelection } = bulkUpdate

  const handleSort = (columnId: string) => {
    if (!onSortChange) return

    const isDefaultColumn = columnId === 'createdAt'

    if (sortBy === columnId) {
      // Currently ascending, switch to descending
      onSortChange(`-${columnId}`)
    } else if (sortBy === `-${columnId}`) {
      // Currently descending
      if (isDefaultColumn) {
        // For the default column, toggle back to ascending
        onSortChange(columnId)
      } else {
        // For other columns, reset to default sort
        onSortChange('-createdAt')
      }
    } else {
      // Not sorted by this column, start with ascending
      onSortChange(columnId)
    }
  }

  const getSortState = (columnId: string): 'asc' | 'desc' | false => {
    if (sortBy === columnId) return 'asc'
    if (sortBy === `-${columnId}`) return 'desc'
    return false
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('externalId', {
        header: 'Load#',
        cell: info => (
          <div className="truncate max-w-[200px]">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('trackingNumber', {
        header: 'PRO#',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('relatedTrackings', {
        header: 'RelatedPROs',
        cell: info =>
          info.getValue()?.map(rt => (
            <span key={rt._id} className="badge badge-sm badge-secondary">
              {rt.trackingNumber}
            </span>
          )),
      }),
      columnHelper.accessor('carrierCode', {
        header: 'SCAC',
        cell: info => (
          <span className="badge badge-sm badge-info uppercase">
            {info.getValue()}
          </span>
        ),
      }),

      columnHelper.accessor('currentStatus.status', {
        header: 'Status',
        cell: info => <TrackingStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('shipmentDetails.origin', {
        header: 'Origin',
        cell: info => {
          const origin = info.getValue()
          return origin?.city && origin?.state ? (
            <span className="text-sm">
              {origin.city}, {origin.state}
            </span>
          ) : null
        },
      }),
      columnHelper.accessor('shipmentDetails.destination', {
        header: 'Destination',
        cell: info => {
          const destination = info.getValue()
          return destination?.city && destination?.state ? (
            <span className="text-sm">
              {destination.city}, {destination.state}
            </span>
          ) : null
        },
      }),
      columnHelper.accessor('pickupDate', {
        header: 'Pickup Date',
        enableSorting: true,
        cell: info => {
          const date = info.getValue()
          return date ? <span className="text-sm">{date}</span> : null
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created At',
        enableSorting: true,
        cell: info => (
          <span className="text-xs">
            {new Date(info.getValue()).toLocaleString()}
          </span>
        ),
      }),
      columnHelper.accessor('lastUpdated', {
        header: 'Last Updated',
        enableSorting: true,
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : null
        },
      }),
      columnHelper.accessor('nextRunDate', {
        header: 'Next Run',
        enableSorting: true,
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : (
            <span className="text-xs opacity-60">Not queued</span>
          )
        },
      }),
      columnHelper.display({
        id: 'selectForUpdate',
        header: 'Update',
        cell: info => {
          const id = info.row.original._id
          return (
            <input
              type="checkbox"
              className="checkbox checkbox-xs"
              checked={selectedIds.has(id)}
              onChange={() => toggleSelection(id)}
              aria-label={`Select tracking ${id}`}
            />
          )
        },
        enableSorting: false,
      }),
    ],
    [sortBy, onSortChange, selectedIds, toggleSelection],
  )

  return (
    <div className="flex flex-col gap-3 h-full">
      <BulkUpdateToolbar state={bulkUpdate} />

      <Table
        data={trackings}
        columns={columns}
        getRowId={tracking => tracking._id}
        selectedId={selectedId}
        onRowClick={onSelect}
        infiniteScrollRef={infiniteScrollRef}
        isFetchingMore={isFetchingMore}
        manualSorting
        onSortChange={handleSort}
        getSortState={getSortState}
      />
    </div>
  )
}

export { TrackingsList }
